import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import { Link } from "react-router-dom";
import { Pagination } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Modal } from "@/components/ui/Modal";
import type { AiRequest, AiRequestsResult } from "@/types";

const ADMIN_AI_REQUESTS_QUERY = gql`
  query AdminAiRequests($page: Int, $perPage: Int, $status: String, $tier: String) {
    adminAiRequests(page: $page, perPage: $perPage, status: $status, tier: $tier) {
      aiRequests {
        id
        serviceName
        aiModel
        prompt
        imageContentType
        imageSizeBytes
        rawResponse
        parsedResult
        resultCount
        durationMs
        status
        tier
        errorMessage
        backtrace
        createdAt
        user {
          id
          displayName
        }
      }
      totalCount
      totalPages
      currentPage
    }
  }
`;

export function AdminAiRequestsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [tierFilter, setTierFilter] = useState<string>("");
  const [selectedRequest, setSelectedRequest] = useState<AiRequest | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["adminAiRequests", page, statusFilter, tierFilter],
    queryFn: () =>
      graphqlClient.request<{ adminAiRequests: AiRequestsResult }>(
        ADMIN_AI_REQUESTS_QUERY,
        {
          page,
          perPage: 25,
          status: statusFilter || undefined,
          tier: tierFilter || undefined,
        }
      ),
  });

  const result = data?.adminAiRequests;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("lt-LT");
  };

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        &larr; Admin valdymo skydas
      </Link>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI užklausos</h1>
          {result && (
            <p className="mt-1 text-sm text-gray-500">
              Iš viso: {result.totalCount}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <select
            value={tierFilter}
            onChange={(e) => {
              setTierFilter(e.target.value);
              setPage(1);
            }}
            className="input w-40"
          >
            <option value="">Visi lygiai</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="input w-40"
          >
            <option value="">Visi statusai</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : result && result.aiRequests.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Data
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Paslauga
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Naudotojas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Modelis
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Lygis
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Trukmė
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Rezultatai
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Statusas
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {result.aiRequests.map((req) => (
                  <tr
                    key={req.id}
                    className="cursor-pointer transition-colors hover:bg-gray-50"
                    onClick={() => setSelectedRequest(req)}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                      {formatDate(req.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {req.serviceName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {req.user?.displayName ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {req.aiModel ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {req.tier ? (
                        <Badge variant={req.tier === "free" ? "info" : "default"}>
                          {req.tier}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {req.durationMs != null ? `${req.durationMs} ms` : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {req.resultCount ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <Badge
                        variant={
                          req.status === "success" ? "success" : "danger"
                        }
                      >
                        {req.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={result.currentPage}
              totalPages={result.totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">Nėra AI užklausų</p>
        </div>
      )}

      {/* Detail Modal */}
      <Modal
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        title="AI užklausos detalės"
        size="4xl"
      >
        {selectedRequest && (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto">
            {/* Meta info grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
              <DetailField label="ID" value={selectedRequest.id} />
              <DetailField label="Data" value={formatDate(selectedRequest.createdAt)} />
              <DetailField label="Statusas">
                <Badge variant={selectedRequest.status === "success" ? "success" : "danger"}>
                  {selectedRequest.status}
                </Badge>
              </DetailField>
              <DetailField label="Paslauga" value={selectedRequest.serviceName} />
              <DetailField label="AI modelis" value={selectedRequest.aiModel ?? "—"} />
              <DetailField label="Lygis">
                {selectedRequest.tier ? (
                  <Badge variant={selectedRequest.tier === "free" ? "info" : "default"}>
                    {selectedRequest.tier}
                  </Badge>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </DetailField>
              <DetailField label="Naudotojas" value={selectedRequest.user?.displayName ?? "—"} />
              <DetailField label="Trukmė" value={selectedRequest.durationMs != null ? `${selectedRequest.durationMs} ms` : "—"} />
              <DetailField label="Rezultatų sk." value={selectedRequest.resultCount?.toString() ?? "—"} />
              {selectedRequest.imageContentType && (
                <>
                  <DetailField label="Vaizdo tipas" value={selectedRequest.imageContentType} />
                  <DetailField label="Vaizdo dydis" value={formatBytes(selectedRequest.imageSizeBytes)} />
                </>
              )}
            </div>

            {/* Error */}
            {selectedRequest.errorMessage && (
              <DetailBlock label="Klaida" variant="error">
                {selectedRequest.errorMessage}
              </DetailBlock>
            )}

            {/* Prompt / Input */}
            {selectedRequest.prompt && (
              <DetailBlock label="Prompt (įvestis)">
                {selectedRequest.prompt}
              </DetailBlock>
            )}

            {/* Raw response */}
            {selectedRequest.rawResponse && (
              <DetailBlock label="Atsakymas (raw)">
                {selectedRequest.rawResponse}
              </DetailBlock>
            )}

            {/* Parsed result */}
            {selectedRequest.parsedResult && (
              <DetailBlock label="Išanalizuotas rezultatas">
                {formatJson(selectedRequest.parsedResult)}
              </DetailBlock>
            )}

            {/* Backtrace */}
            {selectedRequest.backtrace && (
              <DetailBlock label="Backtrace">
                {selectedRequest.backtrace}
              </DetailBlock>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetailField({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">
        {children ?? value}
      </dd>
    </div>
  );
}

function DetailBlock({ label, variant, children }: { label: string; variant?: "error"; children: React.ReactNode }) {
  const bgClass = variant === "error" ? "bg-red-50 text-red-800" : "bg-gray-50 text-gray-700";

  return (
    <div>
      <h4 className={`text-xs font-medium ${variant === "error" ? "text-red-600" : "text-gray-500"}`}>
        {label}
      </h4>
      <pre className={`mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg p-3 text-xs ${bgClass}`}>
        {children}
      </pre>
    </div>
  );
}

function formatJson(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}
